// frontend\src\pages\Profile\Profile.js
import { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/api";
import { toast } from "react-toastify";
import AuthContext from "../../contexts/AuthContext";
import ScrollToTopButton from "../../components/ScrollToTopButton";
import ConfirmModal from "../../components/ConfirmModal";
import { BiCheckCircle, BiUserCircle, BiPaperPlane, BiUnlink, BiLink } from "react-icons/bi";
import NotificationSettings from "./NotificationSettings";
import { useTelegramBot } from '../../hooks/useTelegramBot';

const Profile = () => {
  
  const {
    isAuthenticated,
    username,
    first_name: contextFirstName,
    last_name: contextLastName,
    logoutUser,
    authTokens,
    updateUserData,
  } = useContext(AuthContext);
  
  const { botUsername } = useTelegramBot(authTokens);

  const [formData, setFormData] = useState({
    username: username || "",
    email: "",
    first_name: contextFirstName || "",
    last_name: contextLastName || "",
  });
  const [passwordData, setPasswordData] = useState({
    old_password: "",
    new_password1: "",
    new_password2: "",
  });
  const [telegramData, setTelegramData] = useState({
    is_linked: false,
    telegram_username: "",
    telegram_user_id: null,
  });
  const [linkingToken, setLinkingToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showUnlinkModal, setShowUnlinkModal] = useState(false);
  const navigate = useNavigate();

  // Выносим fetchTelegramStatus в useCallback чтобы избежать warning
  const fetchTelegramStatus = useCallback(async () => {
    try {
      const response = await api.get("/telegram/status/", {
        headers: {
          Authorization: `Bearer ${authTokens?.access}`,
        },
      });
      setTelegramData({
        is_linked: response.data.telegram_linked || response.data.is_linked,
        telegram_username: response.data.telegram_username || "",
        telegram_user_id: response.data.telegram_user_id,
      });
    } catch (error) {
      console.error("Ошибка загрузки статуса Telegram:", error);
      setTelegramData({
        is_linked: false,
        telegram_username: "",
        telegram_user_id: null,
      });
    }
  }, [authTokens]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    const fetchProfile = async () => {
      try {
        // Загружаем основные данные профиля
        const profileResponse = await api.get("/profile/", {
          headers: {
            Authorization: `Bearer ${authTokens?.access}`,
          },
        });
        setFormData({
          username: username || "",
          email: profileResponse.data.email || "",
          first_name: profileResponse.data.first_name || "",
          last_name: profileResponse.data.last_name || "",
        });

        // Отдельно загружаем статус Telegram
        await fetchTelegramStatus();
      } catch (error) {
        console.error("Ошибка загрузки профиля:", error);
        toast.error("Ошибка загрузки данных");
        if (error.response?.status === 401) {
          logoutUser();
        }
      }
    };
    fetchProfile();
  }, [
    isAuthenticated,
    navigate,
    logoutUser,
    authTokens,
    username,
    fetchTelegramStatus,
  ]);

  const generateTelegramLink = async () => {
    setIsLoading(true);
    try {
      const response = await api.post(
        "/telegram/generate-link-token/",
        {},
        {
          headers: {
            Authorization: `Bearer ${authTokens?.access}`,
          },
        }
      );

      setLinkingToken(response.data.token);
      toast.success("Токен создан! Отправьте его боту");

      // Показываем инструкцию
      toast.info(
        <div>
          <p>Отправьте эту ссылку боту:</p>
          <a
            href={response.data.deep_link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-decoration-none"
          >
            {response.data.deep_link}
          </a>
          <p className="mt-2">
            Или напишите боту команду: <code>/start {response.data.token}</code>
          </p>
        </div>,
        { autoClose: 10000 }
      );
    } catch (error) {
      console.error("Ошибка генерации токена:", error);
      toast.error("Ошибка создания ссылки для привязки");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnlinkTelegram = async () => {
    try {
      await api.post(
        "/telegram/unlink-account/",
        {},
        {
          headers: {
            Authorization: `Bearer ${authTokens?.access}`,
          },
        }
      );

      setTelegramData({
        is_linked: false,
        telegram_username: "",
        telegram_user_id: null,
      });
      setLinkingToken("");
      toast.success("Telegram аккаунт отвязан");
      setShowUnlinkModal(false);
    } catch (error) {
      console.error("Ошибка отвязки Telegram:", error);
      toast.error("Ошибка отвязки аккаунта");
    }
  };

  const sendTestMessage = async () => {
    try {
      await api.post(
        "/telegram/send-test-message/",
        {
          message:
            "Привет! Это тестовое сообщение из приложения фотолаборатории.",
        },
        {
          headers: {
            Authorization: `Bearer ${authTokens?.access}`,
          },
        }
      );

      toast.success("Тестовое сообщение отправлено в очередь");
    } catch (error) {
      console.error("Ошибка отправки сообщения:", error);
      toast.error("Ошибка отправки сообщения");
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
  };

  const validatePasswordChange = () => {
    const newErrors = {};

    if (isChangingPassword) {
      if (!passwordData.old_password) {
        newErrors.old_password = "Введите текущий пароль";
      }
      if (passwordData.new_password1 !== passwordData.new_password2) {
        newErrors.new_password2 = "Пароли не совпадают";
      }
      if (passwordData.new_password1.length < 8) {
        newErrors.new_password1 = "Пароль должен содержать минимум 8 символов";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validatePasswordChange()) return;

    try {
      let dataToSend = { ...formData };
      if (isChangingPassword) {
        dataToSend = {
          ...dataToSend,
          old_password: passwordData.old_password,
          new_password: passwordData.new_password1,
        };
      }

      const response = await api.put("/profile/", dataToSend, {
        headers: {
          Authorization: `Bearer ${authTokens?.access}`,
        },
      });

      updateUserData({
        first_name: response.data.first_name,
        last_name: response.data.last_name,
      });

      toast.success("Данные сохранены");
      setPasswordData({
        old_password: "",
        new_password1: "",
        new_password2: "",
      });
      setIsChangingPassword(false);
      navigate("/");
    } catch (error) {
      console.error("Ошибка обновления:", error);
      if (error.response?.data?.old_password) {
        const errorMessage = Array.isArray(error.response.data.old_password)
          ? error.response.data.old_password[0]
          : error.response.data.old_password;
        setErrors({ ...errors, old_password: errorMessage });
      } else if (error.response?.data?.new_password) {
        const errorMessage = Array.isArray(error.response.data.new_password)
          ? error.response.data.new_password[0]
          : error.response.data.new_password;
        setErrors({ ...errors, new_password1: errorMessage });
      } else {
        toast.error("Ошибка обновления профиля");
      }
      if (error.response?.status === 401) {
        logoutUser();
      }
    }
  };

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card mb-4">
            <div className="card-header">
              <h3>
                <BiUserCircle className="me-2" />
                Редактирование профиля
              </h3>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="username" className="form-label">
                    Логин
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="email" className="form-label">
                    Email
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="first_name" className="form-label">
                    Имя
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="last_name" className="form-label">
                    Фамилия
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                  />
                </div>

                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="changePasswordCheck"
                    checked={isChangingPassword}
                    onChange={() => setIsChangingPassword(!isChangingPassword)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="changePasswordCheck"
                  >
                    Изменить пароль
                  </label>
                </div>

                {isChangingPassword && (
                  <>
                    <div className="mb-3">
                      <label htmlFor="old_password" className="form-label">
                        Текущий пароль
                      </label>
                      <input
                        type="password"
                        className={`form-control ${
                          errors.old_password ? "is-invalid" : ""
                        }`}
                        id="old_password"
                        name="old_password"
                        value={passwordData.old_password}
                        onChange={handlePasswordChange}
                      />
                      {errors.old_password && (
                        <div className="invalid-feedback">
                          {errors.old_password}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label htmlFor="new_password1" className="form-label">
                        Новый пароль
                      </label>
                      <input
                        type="password"
                        className={`form-control ${
                          errors.new_password1 ? "is-invalid" : ""
                        }`}
                        id="new_password1"
                        name="new_password1"
                        value={passwordData.new_password1}
                        onChange={handlePasswordChange}
                      />
                      {errors.new_password1 && (
                        <div className="invalid-feedback">
                          {errors.new_password1}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label htmlFor="new_password2" className="form-label">
                        Подтвердите новый пароль
                      </label>
                      <input
                        type="password"
                        className={`form-control ${
                          errors.new_password2 ? "is-invalid" : ""
                        }`}
                        id="new_password2"
                        name="new_password2"
                        value={passwordData.new_password2}
                        onChange={handlePasswordChange}
                      />
                      {errors.new_password2 && (
                        <div className="invalid-feedback">
                          {errors.new_password2}
                        </div>
                      )}
                    </div>
                  </>
                )}

                <button type="submit" className="btn btn-primary">
                  Сохранить
                </button>
              </form>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3>
                <BiPaperPlane className="me-2" /> Telegram Бот
              </h3>
            </div>
            <div className="card-body">
              {telegramData.is_linked ? (
                <div>
                  <div className="alert alert-success">
                    <div className="d-flex align-items-center mb-2">
                      <BiCheckCircle className="me-2" />
                      <strong className="mb-0">Аккаунт привязан</strong>
                    </div>
                    {telegramData.telegram_username && (
                      <p className="mb-1">Username: @{telegramData.telegram_username}</p>
                    )}
                    <small className="text-muted">
                      ID: {telegramData.telegram_user_id}
                    </small>
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-outline-primary"
                      onClick={sendTestMessage}
                    >
                      <BiPaperPlane className="me-1" /> Отправить тестовое
                      сообщение
                    </button>
                    <button
                      className="btn btn-outline-danger"
                      onClick={() => setShowUnlinkModal(true)}
                    >
                      <BiUnlink className="me-1" /> Отвязать аккаунт
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="alert alert-info">
                    <strong>Telegram аккаунт не привязан</strong>
                    <p className="mb-2">
                      Привяжите Telegram бота для получения уведомлений и
                      доступа к вашим данным через мессенджер.
                    </p>
                  </div>

                  <div className="mb-3">
                    <h6>Как привязать:</h6>
                    <ol>
                      <li>
                        Напишите нашему боту в Telegram:{" "}
                        <a
                          href={`https://t.me/${botUsername}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          @{botUsername}
                        </a>
                      </li>
                      <li>Нажмите кнопку ниже чтобы создать токен привязки</li>
                      <li>
                        Отправьте токен боту или перейдите по автоматически
                        созданной ссылке
                      </li>
                      <li>Бот подтвердит успешную привязку</li>
                    </ol>
                  </div>

                  <div className="d-flex gap-2 align-items-center">
                    <button
                      className="btn btn-primary"
                      onClick={generateTelegramLink}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Создание...
                        </>
                      ) : (
                        <>
                          <BiLink className="me-1" /> Создать ссылку для
                          привязки
                        </>
                      )}
                    </button>

                    {linkingToken && (
                      <div className="text-muted">
                        <small>
                          Токен: <code>{linkingToken}</code>
                        </small>
                      </div>
                    )}
                  </div>

                  {linkingToken && (
                    <div className="mt-3 p-3 bg-light rounded">
                      <h6>Быстрая привязка:</h6>
                      <p>Отправьте боту команду:</p>
                      <code className="d-block p-2 bg-dark text-white rounded">
                        /start {linkingToken}
                      </code>
                      <small className="text-muted">
                        Или просто перейдите по{" "}
                        <a
                          href={`https://t.me/${ botUsername }?start=${linkingToken}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          этой ссылке
                        </a>
                      </small>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <NotificationSettings authTokens={authTokens} />
        </div>
      </div>

      {/* Модальное окно подтверждения отвязки */}
      <ConfirmModal
        isOpen={showUnlinkModal}
        onClose={() => setShowUnlinkModal(false)}
        onConfirm={handleUnlinkTelegram}
        title="Отвязка Telegram аккаунта"
        message="Вы уверены, что хотите отвязать Telegram аккаунт? После этого вы перестанете получать уведомления через бота."
        confirmText="Отвязать"
        cancelText="Отмена"
        type="delete"
      />

      <ScrollToTopButton />
    </div>
  );
};

export default Profile;
